import { getStoredActiveAppUser } from '../services/activeAppUser';
import { getRolePermissionsByRoleId } from '../services/userAdministration';
import { roleNameToRoleCode } from './roleService';
import { clearRbacSnapshot, loadRbacSnapshot, saveRbacSnapshot } from './permissionStorage';
import type { RbacSnapshot } from './types';

export type PermissionFetchResult =
  | { ok: true; snapshot: RbacSnapshot }
  | { ok: false; error: string; unauthenticated?: boolean; forbidden?: boolean };

export async function ensureRbacSnapshot(): Promise<PermissionFetchResult> {
  const active = getStoredActiveAppUser();
  if (!active) {
    clearRbacSnapshot();
    return { ok: false, error: 'No active user session found.', unauthenticated: true };
  }

  const cached = loadRbacSnapshot();
  if (cached && cached.roleId === active.defaultRoleId && cached.permissions.length) {
    return { ok: true, snapshot: cached };
  }

  const roleId = active.defaultRoleId;
  if (!Number.isFinite(roleId) || roleId <= 0) {
    clearRbacSnapshot();
    return { ok: false, error: 'Missing roleId from active user.' };
  }

  const perms = await getRolePermissionsByRoleId({ roleId });
  if (!perms.success) {
    clearRbacSnapshot();
    return { ok: false, error: perms.message || 'Failed to load permissions.' };
  }

  const roleName = active.defaultRole?.trim() || active.roleDescription?.trim() || '';
  const snapshot: RbacSnapshot = {
    roleId,
    roleName,
    roleCode: roleNameToRoleCode(roleName),
    permissions: perms.data,
    fetchedAt: Date.now(),
  };
  saveRbacSnapshot(snapshot);
  return { ok: true, snapshot };
}

export function destroyRbacSession(): void {
  clearRbacSnapshot();
}

